import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sparkles, ChevronDown, Lock, CheckCircle2, AlertTriangle, MinusCircle, Plug,
  ArrowRight, HelpCircle, Download, Gauge, TrendingUp,
} from "lucide-react";
import { Loading } from "@/pages/_shared";
import { useEntitlements, trackEvent } from "@/hooks/useEntitlements";
import { StatePill, ProviderCard } from "@/components/membership/Entitlement";
import { UpgradeModal } from "@/components/membership/UpgradeModal";
import { AskModal } from "@/components/ai/AskModal";
import api from "@/lib/api";
import { toast } from "sonner";

function ScoreRing({ value }) {
  const pct = Math.max(0, Math.min(100, value));
  const r = 26, c = 2 * Math.PI * r;
  const off = c - (pct / 100) * c;
  const color = pct >= 75 ? "#10b981" : pct >= 50 ? "#0052ff" : pct >= 30 ? "#f59e0b" : "#ef4444";
  return (
    <svg viewBox="0 0 64 64" className="h-16 w-16 -rotate-90">
      <circle cx="32" cy="32" r={r} fill="none" stroke="#e2e8f0" strokeWidth="6" />
      <circle cx="32" cy="32" r={r} fill="none" stroke={color} strokeWidth="6" strokeLinecap="round"
        strokeDasharray={c} strokeDashoffset={off} className="ring-anim" style={{ "--ring-start": c, "--ring-end": off }} />
    </svg>
  );
}

const TABS = ["Overview", "Credit Factors", "Business Factors", "Cash Flow", "Documents"];
const SCORE_KEYS = ["business_credit.experian", "business_credit.dnb", "business_credit.equifax", "business_credit.fico"];

const STATUS_ICON = {
  strong: CheckCircle2, complete: CheckCircle2, needs_attention: AlertTriangle, missing: MinusCircle,
  not_connected: Plug, not_available: MinusCircle, upgrade_required: Lock, stale: AlertTriangle,
};
const STATUS_TONE = {
  strong: "text-emerald-600", complete: "text-emerald-600", needs_attention: "text-amber-600",
  missing: "text-slate-400", not_connected: "text-[#1a5fd0]", not_available: "text-slate-400",
  upgrade_required: "text-[#b0862f]", stale: "text-amber-600",
};

function FactorRow({ f, onUpgrade, onAsk }) {
  const [open, setOpen] = useState(false);
  const Icon = STATUS_ICON[f.status] || MinusCircle;
  return (
    <div className="border-b border-slate-100 last:border-0" data-testid={`factor-row-${f.id}`}>
      <button onClick={() => { setOpen((v) => !v); if (!open) trackEvent("factor_expanded", { factor: f.id }); }}
        className="flex w-full items-center gap-3 py-3 text-left">
        <Icon size={18} className={`shrink-0 ${STATUS_TONE[f.status] || "text-slate-400"}`} />
        <span className="flex-1 text-[14px] font-medium text-slate-800">{f.name}</span>
        <span className="hidden font-mono text-[12px] text-slate-500 sm:inline">{f.display_value}</span>
        <StatePill state={f.status} labelOverride={f.status === "upgrade_required" ? `${f.required_plan}` : undefined} />
        <ChevronDown size={16} className={`text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="grid gap-3 pb-4 pl-9 sm:grid-cols-2" data-testid={`factor-detail-${f.id}`}>
          <div><div className="font-mono text-[9px] uppercase tracking-wider text-[#1a5fd0]">Your information</div><p className="mt-0.5 text-[13px] text-slate-700">{f.display_value}</p></div>
          <div><div className="font-mono text-[9px] uppercase tracking-wider text-[#1a5fd0]">Source</div><p className="mt-0.5 text-[13px] text-slate-700">{f.source}</p></div>
          <div className="sm:col-span-2"><div className="font-mono text-[9px] uppercase tracking-wider text-[#1a5fd0]">Why this matters</div><p className="mt-0.5 text-[13px] text-slate-600">{f.why}</p></div>
          <div className="flex flex-wrap items-center gap-2 sm:col-span-2">
            {f.status === "upgrade_required"
              ? <button onClick={() => onUpgrade(f)} data-testid={`factor-upgrade-${f.id}`} className="inline-flex items-center gap-1.5 rounded-lg bg-[#b0862f] px-3 py-1.5 text-[12px] font-bold text-white"><Lock size={12} /> Upgrade Membership</button>
              : <span className="text-[13px] font-medium text-slate-700">{f.action}</span>}
            <button onClick={() => onAsk(f)} data-testid={`factor-ask-${f.id}`} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-[12px] font-semibold text-slate-700 hover:bg-slate-50"><Sparkles size={12} /> Ask MMT</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function FundingReadinessPro() {
  const { ent, loading, reload } = useEntitlements();
  const [fr, setFr] = useState(null);
  const [tab, setTab] = useState("Overview");
  const [modal, setModal] = useState(null);
  const [ask, setAsk] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/funding-readiness/full").then(({ data }) => setFr(data)).catch(() => setFr(null));
  }, []);

  const openUpgrade = (feature) => {
    trackEvent("upgrade_required_viewed", { feature: feature.key || feature.id });
    const requiredPlan = feature.required_plan;
    setModal({ feature: feature.key || feature.id, requiredPlan });
  };
  const openAsk = (f) => { trackEvent("ask_mmt_opened", { factor: f?.id }); setAsk(true); };

  const scoreCards = useMemo(() => SCORE_KEYS.map((k) => ent?.features?.[k]).filter(Boolean), [ent]);
  if (loading || !ent || !fr) return <Loading />;

  const groups = {
    high: fr.factors.filter((f) => f.impact === "high"),
    critical: fr.factors.filter((f) => f.impact === "critical"),
    moderate: fr.factors.filter((f) => f.impact === "moderate"),
    low: fr.factors.filter((f) => f.impact === "low"),
  };
  const a = fr.analysis;
  const exportFeat = ent.features["export_report"];

  return (
    <div className="animate-fade-up" data-testid="funding-readiness-pro">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-head text-3xl font-extrabold tracking-tight text-slate-900">Funding <span className="text-foil">Readiness</span></h1>
          <p className="mt-2 max-w-2xl text-[15px] text-slate-500">Understand the factors that may affect your business's financial readiness and see the next steps to strengthen your profile.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => openAsk()} data-testid="ask-mmt-header" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 text-[13px] font-semibold text-slate-700 hover:bg-slate-50"><Sparkles size={14} /> Ask MMT</button>
          {exportFeat?.state === "upgrade_required"
            ? <button onClick={() => openUpgrade(exportFeat)} data-testid="export-locked" className="inline-flex items-center gap-1.5 rounded-lg border border-[#b0862f]/40 px-3 py-2 text-[13px] font-semibold text-[#b0862f]"><Lock size={13} /> Export Report</button>
            : <button onClick={() => toast.success("Preparing your report…")} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 text-[13px] font-semibold text-slate-700 hover:bg-slate-50"><Download size={14} /> Export</button>}
        </div>
      </div>

      {/* Tabs */}
      <div className="mt-5 flex gap-5 overflow-x-auto border-b border-slate-200">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`fr-tab-${t.toLowerCase().replace(/\s/g, "-")}`}
            className={`whitespace-nowrap pb-2.5 text-[14px] font-semibold transition-colors ${tab === t ? "border-b-2 border-[#1a5fd0] text-slate-900" : "text-slate-400 hover:text-slate-600"}`}>{t}</button>
        ))}
      </div>

      {/* MMT Funding Analysis + missing card */}
      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 lg:col-span-2" data-testid="mmt-funding-analysis">
          <div className="flex items-center justify-between">
            <span className="font-head text-[14px] font-bold text-slate-900">MMT Funding Analysis</span>
            <span className="inline-flex items-center gap-1 rounded-md bg-[#1a5fd0]/10 px-2 py-0.5 font-mono text-[10px] font-bold uppercase text-[#1a5fd0]"><Sparkles size={11} /> AI</span>
          </div>
          <div className="mt-3 grid gap-4 sm:grid-cols-2">
            <div>
              <div className="font-mono text-[10px] font-bold uppercase tracking-wider text-emerald-600">Strengths</div>
              <ul className="mt-1 space-y-1 text-[13px] text-slate-700">{a.strengths.length ? a.strengths.map((s) => <li key={s}>✓ {s}</li>) : <li className="text-slate-400">Complete your profile to surface strengths.</li>}</ul>
            </div>
            <div>
              <div className="font-mono text-[10px] font-bold uppercase tracking-wider text-amber-600">Needs attention</div>
              <ul className="mt-1 space-y-1 text-[13px] text-slate-700">{a.needs_attention.length ? a.needs_attention.map((s) => <li key={s}>! {s}</li>) : <li className="text-slate-400">Nothing flagged right now.</li>}</ul>
            </div>
            <div>
              <div className="font-mono text-[10px] font-bold uppercase tracking-wider text-slate-500">Missing</div>
              <ul className="mt-1 space-y-1 text-[13px] text-slate-700">{a.missing.length ? a.missing.map((s) => <li key={s}>○ {s}</li>) : <li className="text-slate-400">—</li>}</ul>
            </div>
            <div>
              <div className="font-mono text-[10px] font-bold uppercase tracking-wider text-[#1a5fd0]">Next best actions</div>
              <ul className="mt-1 space-y-1 text-[13px] text-slate-700">{a.next_actions.length ? a.next_actions.map((s) => <li key={s}>→ {s}</li>) : <li className="text-slate-400">You're all caught up.</li>}</ul>
            </div>
          </div>
          <button onClick={() => openAsk()} className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#1a5fd0] hover:underline"><Sparkles size={13} /> Ask MMT to explain my readiness</button>
        </div>

        <div className="card-spotlight flex flex-col rounded-2xl border border-slate-200 bg-white p-5" data-testid="fr-score-card">
          <div className="flex items-center gap-2 text-slate-400"><Gauge size={16} /><span className="font-mono text-[10px] font-bold uppercase tracking-wider">Your MMT Funding Readiness</span></div>
          {fr.score.available
            ? <div className="mt-3 flex items-center gap-4" data-testid="fr-score-value"><ScoreRing value={fr.score.value} /><div className="font-head text-4xl font-extrabold text-slate-900">{fr.score.value}<span className="text-[15px] text-slate-400">/100</span></div></div>
            : <div className="mt-2 font-head text-[18px] font-bold text-slate-700" data-testid="fr-score-unavailable">Not Yet Available</div>}
          <div className="mt-1 text-[12px] text-slate-500">{fr.score.available ? fr.score.tier : "Complete your profile to calculate your assessment."}</div>
          <div className="mt-4 rounded-xl bg-slate-50 px-3 py-2 text-[13px]">
            <div className="font-semibold text-slate-800" data-testid="missing-count">You're missing {fr.counts.missing} factor{fr.counts.missing !== 1 ? "s" : ""}.</div>
            <button onClick={() => setTab("Business Factors")} className="mt-1 inline-flex items-center gap-1 text-[12px] font-semibold text-[#1a5fd0] hover:underline">Review missing information <ArrowRight size={12} /></button>
          </div>
          {fr.counts.locked > 0 && (
            <div className="mt-3 rounded-xl border border-[#b0862f]/30 bg-[#b0862f]/5 px-3 py-2">
              <div className="text-[12px] font-semibold text-[#8a6a25]">Your plan doesn't include {fr.counts.locked} factor{fr.counts.locked !== 1 ? "s" : ""}.</div>
              <button onClick={() => { const lf = fr.factors.find((x) => x.status === "upgrade_required"); if (lf) openUpgrade(lf); }}
                data-testid="unlock-more-btn" className="mt-1.5 inline-flex items-center gap-1.5 rounded-lg bg-[#b0862f] px-3 py-1.5 text-[12px] font-bold text-white"><Lock size={12} /> Upgrade Membership</button>
            </div>
          )}
          <div className="mt-3 font-mono text-[9px] uppercase tracking-wider text-slate-400">{fr.model_version}</div>
        </div>
      </div>

      {/* Business credit scores */}
      <section className="mt-8">
        <h2 className="font-head text-lg font-extrabold text-slate-900">Business credit scores</h2>
        <div className="mt-3 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {scoreCards.map((f) => (
            <ProviderCard key={f.key} feature={f} onUpgrade={openUpgrade}
              onWaitlist={() => toast.success("We'll notify you when this is available.")}
              onConnect={() => toast.info("Connection opens once MMT enables this provider.")} />
          ))}
        </div>
      </section>

      {/* Factor groups */}
      {[["High impact", groups.high], ["Critical attention", groups.critical], ["Moderate impact", groups.moderate]].map(([title, list]) => (
        list.length > 0 && (
          <section key={title} className="mt-8">
            <h2 className="font-head text-lg font-extrabold text-slate-900">{title}</h2>
            <div className="mt-2 rounded-2xl border border-slate-200 bg-white px-4">{list.map((f) => <FactorRow key={f.id} f={f} onUpgrade={openUpgrade} onAsk={openAsk} />)}</div>
          </section>
        )
      ))}

      {groups.low.length > 0 && (
        <details className="mt-8 rounded-2xl border border-slate-200 bg-white px-4 py-3" data-testid="lower-impact">
          <summary className="cursor-pointer font-head text-[14px] font-bold text-slate-900">Lower impact factors ({groups.low.length})</summary>
          <div className="mt-1">{groups.low.map((f) => <FactorRow key={f.id} f={f} onUpgrade={openUpgrade} onAsk={openAsk} />)}</div>
        </details>
      )}

      {/* Funding opportunities */}
      <section className="mt-8">
        <h2 className="font-head text-lg font-extrabold text-slate-900">Funding opportunities</h2>
        <div className="mt-3 rounded-2xl border border-slate-200 bg-white p-6 text-center" data-testid="funding-opportunities">
          <TrendingUp size={22} className="mx-auto text-[#1a5fd0]" />
          <p className="mx-auto mt-2 max-w-md text-[14px] text-slate-600">
            {fr.score.available
              ? "Based on the information currently available, explore funding categories that may fit your profile."
              : "Complete your profile to discover potential funding opportunities."}
          </p>
          <button onClick={() => { trackEvent("marketplace_clicked"); navigate("/funding-options"); }} data-testid="explore-marketplace"
            className="mt-3 inline-flex items-center gap-2 rounded-xl bg-[#1a5fd0] px-5 py-2.5 font-head text-[14px] font-bold text-white hover:bg-[#1550b8]">Explore Funding Marketplace <ArrowRight size={16} /></button>
        </div>
      </section>

      {/* Unlock more */}
      {fr.counts.locked > 0 && (
        <section className="mt-8 rounded-2xl border border-[#b0862f]/30 bg-gradient-to-br from-[#b0862f]/5 to-transparent p-6" data-testid="unlock-more-section">
          <h2 className="font-head text-lg font-extrabold text-slate-900">Unlock more of your financial profile</h2>
          <p className="mt-1 max-w-xl text-[14px] text-slate-600">Your current membership doesn't include {fr.counts.locked} readiness factor{fr.counts.locked !== 1 ? "s" : ""}. Upgrade to access additional insights and eligible integrations.</p>
          <button onClick={() => { const lf = fr.factors.find((x) => x.status === "upgrade_required"); if (lf) openUpgrade(lf); }}
            className="mt-3 inline-flex items-center gap-2 rounded-xl bg-[#b0862f] px-5 py-2.5 font-head text-[14px] font-bold text-white"><Lock size={15} /> Upgrade Membership</button>
        </section>
      )}

      <div className="mt-6 flex items-start gap-2 rounded-lg bg-slate-50 px-4 py-3 text-[12px] text-slate-500">
        <HelpCircle size={15} className="mt-0.5 shrink-0" />
        MMT Funding Readiness is an internal readiness assessment — not a lender underwriting score, a bureau score, an approval, or a guarantee.
      </div>

      {modal && <UpgradeModal open onClose={() => { setModal(null); reload(); }} feature={modal.feature} requiredPlan={modal.requiredPlan} returnRoute="/funding-readiness" ent={ent} />}
      <AskModal open={ask} onClose={() => setAsk(false)} />
    </div>
  );
}
